import { Link } from 'react-router-dom';
import { QuizEngine } from '../components/QuizEngine';

const questions = [
  {
    question: 'Wie lange planst du, in derselben Stadt oder Region zu bleiben?',
    options: [
      'Weniger als 3 Jahre',
      '3-7 Jahre',
      '7-15 Jahre',
      'Mehr als 15 Jahre oder dauerhaft'
    ],
    correctAnswer: 3,
    explanation: 'Je länger du planst zu bleiben, desto eher lohnt sich der Kauf. Die Kaufnebenkosten (ca. 10-12%) amortisieren sich erst nach mehreren Jahren.'
  },
  {
    question: 'Wie viel Eigenkapital kannst du einbringen?',
    options: [
      'Weniger als 10% des Kaufpreises',
      '10-20% des Kaufpreises',
      '20-30% des Kaufpreises',
      'Mehr als 30% des Kaufpreises'
    ],
    correctAnswer: 2,
    explanation: 'Experten empfehlen mindestens 20% Eigenkapital plus Nebenkosten. Das senkt die monatliche Belastung und verbessert die Kreditkonditionen.'
  },
  {
    question: 'Wie wichtig ist dir Flexibilität bei Wohnungswechseln?',
    options: [
      'Sehr wichtig – ich möchte jederzeit umziehen können',
      'Wichtig, aber ich könnte mich auch binden',
      'Weniger wichtig – Stabilität ist mir wichtiger',
      'Unwichtig – ich suche mein Zuhause fürs Leben'
    ],
    correctAnswer: 3,
    explanation: 'Miete bietet maximale Flexibilität. Beim Kauf bist du gebunden – ein Verkauf dauert Zeit und kostet Geld.'
  },
  {
    question: 'Wie ist deine berufliche Situation?',
    options: [
      'Selbstständig oder unsichere Einkommenssituation',
      'Angestellt, aber noch in der Probezeit oder befristet',
      'Unbefristet angestellt seit kurzem',
      'Unbefristet angestellt seit mehreren Jahren'
    ],
    correctAnswer: 3,
    explanation: 'Banken bevorzugen stabile Einkommensverhältnisse. Unbefristete Anstellungen erleichtern die Kreditvergabe erheblich.'
  },
  {
    question: 'Was würdest du mit einer unerwarteten Erbschaft von 50.000 € machen?',
    options: [
      'Urlaub, Auto oder andere Anschaffungen',
      'Anlegen und für später sparen',
      'Als Anzahlung für eine Immobilie nutzen',
      'Bestehenden Kredit tilgen oder in Immobilie investieren'
    ],
    correctAnswer: 2,
    explanation: 'Deine Antwort zeigt deine Einstellung zu Immobilien als Investment. Wer sofort an Eigentum denkt, ist tendenziell eher ein Käufer-Typ.'
  },
  {
    question: 'Wie gehst du mit Reparaturen und Instandhaltung um?',
    options: [
      'Das ist Sache des Vermieters – ich rufe an',
      'Kleinigkeiten erledige ich selbst, größeres überlasse ich Profis',
      'Ich bin handwerklich begabt und mache vieles selbst',
      'Ich plane regelmäßig Rücklagen für Instandhaltung ein'
    ],
    correctAnswer: 3,
    explanation: 'Als Eigentümer musst du für alle Reparaturen selbst aufkommen. Experten empfehlen 1-2% des Immobilienwerts pro Jahr als Rücklage.'
  }
];

const resultMessages = {
  excellent: 'Du hast das Zeug zum Immobilienbesitzer! Deine Antworten zeigen, dass Kaufen für dich eine gute Option sein könnte.',
  good: 'Eine Mischung aus beiden Welten – informiere dich gut und rechne beide Szenarien durch.',
  average: 'Aktuell scheint Mieten für dich die flexiblere Option zu sein.',
  needsWork: 'Mieten ist wahrscheinlich erstmal die bessere Wahl. Das kann sich aber ändern!'
};

const relatedLinks = [
  { path: '/tippspiele/was-kostet-dieses-haus', label: 'Was kostet dieses Haus?' },
  { path: '/tippspiele/neubau-oder-sanierung', label: 'Neubau oder Sanierung?' },
  { path: '/suedtirol/wohnen-in-suedtirol', label: 'Wohnen in Südtirol' }
];

export default function WohnungKaufenOderMieten() {
  return (
    <div className="section-spacing" data-testid="kaufen-mieten-page">
      <div className="container-wide">
        {/* Breadcrumb */}
        <nav className="text-sm text-[#64748B] mb-8" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-[#0284C7]">Start</Link>
          <span className="mx-2">/</span>
          <Link to="/immobilien" className="hover:text-[#0284C7]">Immobilien</Link>
          <span className="mx-2">/</span>
          <span className="text-[#0F172A]">Wohnung kaufen oder mieten?</span>
        </nav>

        <QuizEngine
          title="Kaufen oder Mieten?"
          introText="Die ewige Frage – beantworte 6 Fragen zu deiner Situation und erhalte eine erste Einschätzung. Kein Ersatz für eine professionelle Beratung, aber ein guter Startpunkt!"
          questions={questions}
          resultMessages={resultMessages}
          relatedLinks={relatedLinks}
        />

        {/* SEO Content */}
        <div className="mt-16 advisor-content">
          <h2>Kaufen oder Mieten in Südtirol?</h2>
          <p>
            Diese Frage lässt sich nicht pauschal beantworten – sie hängt von deiner 
            persönlichen Situation, deinen Zielen und dem lokalen Markt ab. In Südtirol 
            gibt es einige Besonderheiten zu beachten.
          </p>
          
          <h3>Der Südtiroler Immobilienmarkt</h3>
          <p>
            Südtirol hat einen der teuersten Immobilienmärkte Italiens. Die Kaufpreise 
            sind hoch, aber auch die Mieten. Das relativiert den klassischen Vergleich 
            „Miete ist rausgeworfenes Geld" etwas.
          </p>

          <h3>Wann lohnt sich Kaufen?</h3>
          <ul>
            <li>Du planst langfristig in der Region zu bleiben (7+ Jahre)</li>
            <li>Du hast ausreichend Eigenkapital (mindestens 20%)</li>
            <li>Dein Einkommen ist stabil und planbar</li>
            <li>Du siehst die Immobilie auch als Altersvorsorge</li>
          </ul>

          <h3>Wann ist Mieten besser?</h3>
          <ul>
            <li>Du brauchst Flexibilität (beruflich oder privat)</li>
            <li>Das Eigenkapital ist noch nicht ausreichend</li>
            <li>Du möchtest nicht alle Risiken selbst tragen</li>
            <li>Der Kaufpreis-Miete-Faktor ist ungünstig (über 25)</li>
          </ul>

          <p>
            Du suchst eine Wohnung in Südtirol – egal ob zum Kaufen oder Mieten? 
            Bei{' '}
            <a href="https://amadeus.immo" target="_blank" rel="noopener noreferrer">
              amadeus.immo
            </a>{' '}
            findest du aktuelle Angebote und kompetente Beratung.
          </p>

          <div className="cta-box">
            <div className="cta-box-title">Noch unentschlossen?</div>
            <p className="cta-box-text">
              Unser Schätzspiel hilft dir, ein besseres Gefühl für Immobilienpreise 
              in Südtirol zu entwickeln.
            </p>
            <Link to="/tippspiele/was-kostet-dieses-haus" className="text-[#0284C7] font-medium hover:underline">
              Was kostet dieses Haus? →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
